import React, { useEffect, useState, useCallback } from "react";
import TopNav from "../layouts/TopNav";
import "../Styles/invoiceTable.css";
import { ToastContainer, toast } from "react-toastify";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate, Link } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import ReLoginModal from "../components/ReLoginModal";
import { CircularProgress } from "@mui/material";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import SolarMenu from "../asset/svg/solarMenu";
import CustomModal from "../components/CustomModal";
import { SidebarModal } from "../components/Modals/sidebarModal";
import CustomReturnModal from "../components/CustomReturnModal";
import api from "../util/axios";

type Customer = {
  _id: string;
  businessName: string;
};

type Requisition = {
  _id: string;
  approvalNumber: string;
  availableToBook: number;
  customer: Customer;
  itemId: string;
  itemName: string;
  quantity: number;
  quantityToLoad: number;
  totalCost: number;
  unitPrice: number;
  quantityLoaded: number;
};

type LoadingSlip = {
  _id: string;
  requisitions: Requisition[];
  truckNumber: string;
  invoiced: boolean;
  destination: string;
};

type Invoice = {
  _id: string;
  name: string;
  description: string;
  unitOfMeasure: string;
  category: string;
  price: number;
  quantity: string;
  loadingSlip: LoadingSlip;
  updatedAt: string;
  returned: boolean;
};

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const InvoiceTable = () => {
  const navigate = useNavigate();
  const [reLogin, setReLogin] = useState(false);
  const [getInvoice, setGetInvoice] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [truckQuery, setTruckQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [modalDisplay, setModalDisplay] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    customerName: string;
    itemName: string;
    unitPrice: number;
    quantityToLoad: number;
    quantityLoaded: number;
    totalCost: number;
    truckNumber: string;
    destination: string;
    loadingSlipId: string; 
  }>({
    customerName: "",
    itemName: "",
    unitPrice: 0,
    quantityToLoad: 0,
    quantityLoaded: 0,
    totalCost: 0,
    truckNumber: "",
    destination: "",
    loadingSlipId: "",
  });

  const userRole = window.localStorage.getItem("userRole");
  const isAdmin = userRole && userRole.toLowerCase() === "admin";
  const isDisabled = !isAdmin || isLoading;

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}`;

  const fetchInvoiceData = async (pageNumber: number, pageSize: number) => {
    setIsLoading(true);
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      setIsLoading(false);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const startDate = fromDate || "2023-01-07";
    const endDate = toDate || formattedDate;
    const queryParams = `?limit=${pageSize}&startDate=${startDate}&endDate=${endDate}&page=${pageNumber}`;

    try {
      const response = await api.get(
        `/invoice${queryParams}`,
        { headers }
      );
      // console.log("invoices:", response.data.data.invoice);
      setGetInvoice(response.data.data.invoice);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch invoices";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {

        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error(errorMessage || "Failed to fetch data. Please check your internet connection.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData(page + 1, rowsPerPage);
  }, [page, rowsPerPage, fromDate, toDate]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateInvoice = async (id: string) => {
    setIsLoading(true);
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await api.get(
        `/invoice/${id}`,
        { headers }
      );
      setIsLoading(false);
      // console.log()
      navigate("/dashboard/invoice", { state: response.data.data });
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch invoice details";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {

        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error(errorMessage);
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    // console.log("handleDeleteInvoice triggered for id:", id);
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    try {
      // console.log("Sending DELETE request for invoice:", id);
      await api.delete(
        `/invoice/${id}`,
        { headers }
      );
      toast.success("Invoice deleted successfully");
      setModalOpen(false);
      fetchInvoiceData(page + 1, rowsPerPage);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to delete invoice";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {

        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error(errorMessage);
    }
  };

  const handleReturnInvoice = async (id: string) => {
    console.log("handleReturnInvoice triggered for id:", id);
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    try {
      console.log("Sending Return request for invoice:", id);
      const response = await api.patch(
        `/invoice/${id}/return`,
        { 'reason': "Error in invoice" },
        { headers }
      );
      toast.success("Invoice Returned Successfully");
      setModalOpen(false);
      fetchInvoiceData(page + 1, rowsPerPage);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to return invoice";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {
        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error(errorMessage);
    }
  };

  const handleUpdateInvoice = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.loadingSlipId) {
      toast.error("No loading slip selected for update.");
      return;
    }

    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }

    setIsLoading(true);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await api.patch(
        `/loading-slip/reprint/${formData.loadingSlipId}`,
        {
          truckNumber: formData.truckNumber,
          destination: formData.destination,
        },
        { headers }
      );
      toast.success("Loading slip updated successfully");
      setModalDisplay(false);
      fetchInvoiceData(page + 1, rowsPerPage);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update loading slip";
      console.error("Update error:", errorMessage);
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {

        setReLogin(true);
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [formData, page, rowsPerPage]);

  useEffect(() => {
    if (selectedInvoiceId && modalDisplay) {
      const invoice = getInvoice.find((inv) => inv._id === selectedInvoiceId);
      if (invoice) {
        const requisition = invoice.loadingSlip.requisitions[0] || {};
        setFormData({
          customerName: requisition.customer?.businessName || "N/A",
          itemName: requisition.itemName || "N/A",
          unitPrice: requisition.unitPrice || 0,
          quantityToLoad: requisition.quantityToLoad || 0,
          quantityLoaded: requisition.quantityLoaded || 0,
          totalCost: requisition.totalCost || 0,
          truckNumber: invoice.loadingSlip.truckNumber || "",
          destination: invoice.loadingSlip.destination || "",
          loadingSlipId: invoice.loadingSlip._id || "",
        });
      } else {
        toast.error("Selected invoice not found. Please try again.");
        setModalDisplay(false);
      }
    }
  }, [selectedInvoiceId, modalDisplay, getInvoice]);

  const filteredInvoices = getInvoice.filter((invoice: Invoice) => {
    if (searchField !== "combined" && !searchQuery) return true;
    if (
      searchField === "combined" &&
      !businessQuery &&
      !productQuery &&
      !truckQuery
    )
      return true;

    const requisitions = invoice.loadingSlip.requisitions;
    const truckNumber = invoice.loadingSlip.truckNumber
      ?.toString()
      .toLowerCase();
    const businessName = requisitions[0]?.customer?.businessName?.toLowerCase();
    const productName = requisitions[0]?.itemName?.toLowerCase();

    const businessNameMatch =
      searchField === "combined"
        ? businessName?.includes(businessQuery.toLowerCase())
        : businessName?.includes(searchQuery.toLowerCase());
    const productNameMatch =
      searchField === "combined"
        ? productName?.includes(productQuery.toLowerCase())
        : productName?.includes(searchQuery.toLowerCase());
    const truckNumberMatch =
      searchField === "combined"
        ? truckNumber?.includes(truckQuery.toLowerCase())
        : truckNumber?.includes(searchQuery.toLowerCase());

    switch (searchField) {
      case "businessName":
        return businessNameMatch;
      case "productName":
        return productNameMatch;
      case "truckNumber":
        return truckNumberMatch;
      case "combined":
        return businessNameMatch && productNameMatch && truckNumberMatch;
      default:
        return businessNameMatch;
    }
  });

  return (
    <div className="board">
      <ToastContainer />
      <TopNav title="Invoice" />
      {reLogin ? <ReLoginModal /> : null}
      <div
        className="method"
        style={{ justifyContent: "flex-end", marginTop: "15px" }}
      >
        <div className="filterBy">
          <strong>Filter by: </strong>
          <span>Start date: </span>
          <input
            type="date"
            name="startDate"
            id=""
            onChange={(e) => setFromDate(e.target.value)}
          />
          <span>End date: </span>
          <input
            type="date"
            name="endDate"
            id=""
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Item Name</option>
            <option value="truckNumber">Truck Number</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <input
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Item Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Truck Number"
              value={truckQuery}
              onChange={(e) => setTruckQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <input
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
      </div>
      <div className="legend" style={{ marginTop: "10px", fontSize: "0.9em", color: "#555" }}>
        <p>
          <span className="merged-badge">Merged</span> indicates loading slips with multiple requisitions, highlighted in yellow. <span className="returned-badge">Returned</span> indicates invioces that have been returned, highlighted in red.
        </p>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          marginTop: "2em",
        }}
      >
        <Link to="/dashboard/notinvoiced">
          <button
            style={{
              width: "120px",
              height: "30px",
              flexShrink: "0",
              borderRadius: "6px",
              background: "red",
              border: "none",
              color: "#fff",
              fontSize: "13px",
              fontStyle: "normal",
              fontWeight: "500",
              lineHeight: "normal",
              cursor: "pointer",
            }}
          >
            Not invoiced
          </button>
        </Link>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : (
          <table className="inTable">
            <thead>
              <tr className="heTable">
                <th>Customer Name</th>
                <th>Item Name</th>
                <th>Unit Price</th>
                <th>Truck Number</th>
                <th>Quantity to Load</th>
                <th>Quantity Loaded</th>
                <th>Total Cost</th>
                <th className="smm">Invoice</th>
                <th className="smm">Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length > 0 ? (
                filteredInvoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((invoice, index) => {
                    const loadingSlip = invoice.loadingSlip;
                    const requisitions = loadingSlip.requisitions;
                    const isMerged = requisitions.length > 1;
                    const isReturned = invoice.returned;
                    const totalQuantityToLoad = requisitions.reduce(
                      (acc, req) => acc + req.quantityToLoad,
                      0
                    );
                    const totalQuantityLoaded = requisitions.reduce(
                      (acc, req) => acc + req.quantityLoaded,
                      0
                    );
                    const totalCost = requisitions.reduce(
                      (acc, req) => acc + req.totalCost,
                      0
                    );
                    const unitPrice = requisitions[0]?.unitPrice || "N/A";
                    const formattedDate = new Date(invoice.updatedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit"
                    });

                    return (
                      <tr key={index} className={`taData ${isMerged ? "merged" : ""}`}>
                        <td>
                          {requisitions[0]?.customer?.businessName || "N/A"}
                          {isMerged && <span className="merged-badge ">Merged</span>}
                          {isReturned && <span className="returned-badge" style={{textTransform: 'capitalize', fontSize: '8px'}}>Returned</span>}
                        </td>
                        <td>{requisitions[0]?.itemName || "N/A"}</td>
                        <td>{unitPrice}</td>
                        <td>{loadingSlip.truckNumber || "N/A"}</td>
                        <td>{totalQuantityToLoad}</td>
                        <td>{totalQuantityLoaded}</td>
                        <td>{totalCost}</td>
                        <td className="smm">{loadingSlip.invoiced.toString()}</td>
                        <td className="smm">{formattedDate}</td>
                        <td>
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                              <button className="">
                                <SolarMenu />
                              </button>
                            </DropdownMenu.Trigger>

                            <DropdownMenu.Portal>
                              <DropdownMenu.Content
                                className="bg-white rounded shadow p-2"
                                sideOffset={5}
                              >
                                <DropdownMenu.Item
                                  className="px-2 text-blue-500 py-2 rounded hover:bg-gray-100 focus:outline-none cursor-pointer"
                                  onSelect={() => {
                                    setSelectedInvoiceId(invoice._id);
                                    setModalDisplay(true);
                                  }}
                                >
                                  View / Edit
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  className="px-2 py-2 text-blue-500 rounded hover:bg-gray-100 focus:outline-none cursor-pointer"
                                  onSelect={() => {
                                    handleCreateInvoice(invoice._id);
                                  }}
                                >
                                  <button
                                    className=""
                                    disabled={!loadingSlip.invoiced}
                                    style={!loadingSlip.invoiced ? { cursor: "not-allowed", opacity: 0.5 } : {}}
                                  >
                                    Print
                                  </button>
                                </DropdownMenu.Item>
                                <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
                                {userRole === "admin" ? (
                                  <DropdownMenu.Item
                                    className="px-2 py-2 rounded hover:bg-red-100 text-red-600 focus:outline-none cursor-pointer"
                                    onSelect={() => {
                                      setSelectedInvoiceId(invoice._id);
                                      setReturnModalOpen(true);
                                    }}
                                  >
                                    Return quantity
                                  </DropdownMenu.Item>
                                ) : null}
                                {userRole === "admin" ? (
                                  <DropdownMenu.Item
                                    className="px-2 py-2 rounded hover:bg-red-100 text-red-600 focus:outline-none cursor-pointer"
                                    onSelect={() => {
                                      setSelectedInvoiceId(invoice._id);
                                      setModalOpen(true);
                                    }}
                                  >
                                    Delete
                                  </DropdownMenu.Item>
                                ) : null}
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                          <CustomReturnModal
                            isOpen={returnModalOpen}
                            onClose={() => {
                              setReturnModalOpen(false);
                            }}
                            onConfirm={() => {
                              if (selectedInvoiceId) handleReturnInvoice(selectedInvoiceId);
                              setReturnModalOpen(false);
                            }}
                            invoiceId={selectedInvoiceId || ""}
                          />
                          <CustomModal
                            isOpen={modalOpen}
                            onClose={() => {
                              setModalOpen(false);
                            }}
                            onConfirm={() => {
                              if (selectedInvoiceId) handleDeleteInvoice(selectedInvoiceId);
                            }}
                            invoiceId={selectedInvoiceId || ""}
                          />
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <SidebarModal
        title="View / Edit Invoice"
        display={modalDisplay}
        close={() => {
          setModalDisplay(false);
          setFormData({
            customerName: "",
            itemName: "",
            unitPrice: 0,
            quantityToLoad: 0,
            quantityLoaded: 0,
            totalCost: 0,
            truckNumber: "",
            destination: "",
            loadingSlipId: "",
          });
        }}
      >
        <div className="invoice-modal-content" style={{ padding: "20px" }}>
          <form onSubmit={handleUpdateInvoice}>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Customer Name
              </label>
              <input
                type="text"
                value={formData.customerName}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "not-allowed",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Item Name
              </label>
              <input
                type="text"
                value={formData.itemName}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "not-allowed",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Unit Price
              </label>
              <input
                type="number"
                value={formData.unitPrice}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "not-allowed",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Quantity to Load
              </label>
              <input
                type="number"
                value={formData.quantityToLoad}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "not-allowed",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Quantity Loaded
              </label>
              <input
                type="number"
                value={formData.quantityLoaded}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "not-allowed",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Total Cost
              </label>
              <input
                type="number"
                value={formData.totalCost}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "not-allowed",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Truck Number
              </label>
              <input
                type="text"
                value={formData.truckNumber}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    truckNumber: e.target.value,
                  }))
                }
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Destination
              </label>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    destination: e.target.value,
                  }))
                }
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="submit"
                disabled={isDisabled}
                style={{
                  padding: "10px 20px",
                  backgroundColor: isLoading ? "#0B77C5" : "#0B77C5",
                  color: "#fff",
                  border: "none",
                  width: "50%",
                  borderRadius: "4px",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                }}
              >
                {isLoading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Edit"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setModalDisplay(false);
                  setFormData({
                    customerName: "",
                    itemName: "",
                    unitPrice: 0,
                    quantityToLoad: 0,
                    quantityLoaded: 0,
                    totalCost: 0,
                    truckNumber: "",
                    destination: "",
                    loadingSlipId: "",
                  });
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ccc",
                  color: "#000",
                  width: "50%",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </SidebarModal>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredInvoices.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default InvoiceTable;